import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr, fdate } from "@/api";
import { PageHeader, EmptyState } from "@/components/Shared";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, ShoppingCart, Trash2, Pencil } from "lucide-react";

const selCls = "w-full h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900";
const GST_SLABS = [0, 5, 12, 18, 28];

export default function Purchases() {
  const { user } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [vendorId, setVendorId] = useState("");
  const [items, setItems] = useState([{ product_id: "", qty: 1, rate: 0, gst_rate: null }]);
  const [paid, setPaid] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/purchases").then((r) => setPurchases(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/banks").then((r) => setBanks(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setEditId(null); setVendorId(""); setItems([{ product_id: "", qty: 1, rate: 0, gst_rate: null }]);
    setPaid(false); setAccountId(""); setDate("");
  };

  const openNew = () => { resetForm(); setOpen(true); };

  const openEdit = (p) => {
    setEditId(p.purchase_id);
    setVendorId(p.vendor_id);
    setItems(p.items.map((it) => ({ product_id: it.product_id, qty: it.qty, rate: it.rate, gst_rate: it.gst_rate ?? null })));
    setPaid(Boolean(p.paid));
    setAccountId(p.account_id || "");
    setDate(p.date || "");
    setOpen(true);
  };

  const setItem = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    if (k === "product_id") {
      const p = products.find((x) => x.product_id === v);
      if (p) {
        next[i].rate = p.purchase_cost || p.prices?.distributor || 0;
        next[i].gst_rate = p.gst_rate ?? 0;
      }
    }
    setItems(next);
  };

  const effGst = (it) => {
    if (it.gst_rate !== null && it.gst_rate !== undefined && it.gst_rate !== "") return Number(it.gst_rate);
    return Number(products.find((x) => x.product_id === it.product_id)?.gst_rate || 0);
  };
  const taxable = items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.rate || 0), 0);
  const gstTotal = items.reduce((s, it) => s + (Number(it.qty || 0) * Number(it.rate || 0) * effGst(it)) / 100, 0);
  const total = taxable + gstTotal;

  const save = async (e) => {
    e.preventDefault();
    if (!vendorId) return toast.error("Select a vendor");
    if (items.some((it) => !it.product_id || Number(it.qty) <= 0)) return toast.error("Complete all item rows");
    if (paid && !accountId) return toast.error("Select the bank account used for payment");
    setBusy(true);
    try {
      const payload = {
        vendor_id: vendorId, paid, account_id: paid ? accountId : null,
        items: items.map((it) => ({ product_id: it.product_id, qty: Number(it.qty), rate: Number(it.rate), gst_rate: effGst(it) })),
        date: date || undefined,
      };
      if (editId) {
        await api.put(`/purchases/${editId}`, payload);
        toast.success("Purchase updated — stock & vendor ledger recalculated");
      } else {
        await api.post("/purchases", payload);
        toast.success("Purchase posted — stock updated, GST recorded as ITC, vendor ledger credited");
      }
      setOpen(false); resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${editId ? "update" : "post"} purchase`);
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Permanently delete this purchase from ${p.vendor_name} (${inr(p.total)})? Stock will be reduced and the vendor ledger reversed.`)) return;
    try {
      await api.delete(`/purchases/${p.purchase_id}`);
      toast.success("Purchase deleted — stock reduced, ledger reversed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="purchases-page">
      <PageHeader title="Purchase Entries" sub="Buying from a vendor increases stock · GST slab per item counts as ITC · paid entries show in Income & Expense">
        <Button onClick={openNew} data-testid="add-purchase-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
          <Plus className="w-4 h-4 mr-1" /> New Purchase
        </Button>
      </PageHeader>

      {purchases.length === 0 ? (
        <EmptyState icon={ShoppingCart} title="No purchases yet" sub="Record a purchase to build stock" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="purchases-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Date</th><th className="px-5 py-3">Vendor</th>
                <th className="px-5 py-3">Items</th><th className="px-5 py-3 text-right">Taxable</th>
                <th className="px-5 py-3 text-right">GST (ITC)</th><th className="px-5 py-3 text-right">Total</th>
                <th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {purchases.map((p) => (
                <tr key={p.purchase_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`purchase-row-${p.purchase_id}`}>
                  <td className="px-5 py-3.5 text-slate-600">{fdate(p.date)}</td>
                  <td className="px-5 py-3.5 font-medium text-slate-900">{p.vendor_name}</td>
                  <td className="px-5 py-3.5 text-slate-600">{p.items.map((i) => `${i.name} ×${i.qty} @${i.gst_rate ?? 0}%`).join(", ")}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 text-slate-700">{inr(p.subtotal ?? p.total)}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 text-blue-600">{inr(p.gst_total ?? 0)}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 font-bold text-slate-900">{inr(p.total)}</td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full border text-[11px] font-mono2 uppercase ${p.paid ? "border-emerald-500/40 text-emerald-600 bg-emerald-500/10" : "border-amber-500/40 text-amber-600 bg-amber-500/10"}`}>
                      {p.paid ? "Paid" : "Payable"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(p)} data-testid={`purchase-edit-${p.purchase_id}`}
                      className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    {user?.role === "admin" && (
                      <button onClick={() => remove(p)} data-testid={`purchase-delete-${p.purchase_id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl" data-testid="add-purchase-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Purchase Entry" : "New Purchase Entry"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Vendor *</Label>
                <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="purchase-vendor-select" className={selCls}>
                  <option value="">Select vendor…</option>
                  {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Purchase Date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="purchase-date-input"
                  className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Payment</Label>
              <div className="flex items-center gap-3 h-10">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} data-testid="purchase-paid-checkbox" className="accent-emerald-500 w-4 h-4" />
                  Paid now
                </label>
                {paid && (
                  <select value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="purchase-account-select" className={selCls}>
                    <option value="">From account…</option>
                    {banks.map((b) => <option key={b.account_id} value={b.account_id}>{b.name}</option>)}
                  </select>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-xs font-mono2 uppercase tracking-wider text-slate-500 px-1">
                <span className="col-span-5">Product</span><span className="col-span-2">Qty</span>
                <span className="col-span-2">Rate ₹</span><span className="col-span-2">GST %</span><span className="col-span-1" />
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`purchase-item-row-${i}`}>
                  <select value={it.product_id} onChange={(e) => setItem(i, "product_id", e.target.value)} data-testid={`purchase-item-product-${i}`} className={`${selCls} col-span-5`}>
                    <option value="">Select product…</option>
                    {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
                  </select>
                  <Input type="number" step="any" min="0" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`purchase-item-qty-${i}`} className="col-span-2 bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
                  <Input type="number" step="0.01" min="0" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} data-testid={`purchase-item-rate-${i}`} className="col-span-2 bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
                  <select value={effGst(it)} onChange={(e) => setItem(i, "gst_rate", Number(e.target.value))} data-testid={`purchase-item-gst-${i}`} className={`${selCls} col-span-2`}>
                    {GST_SLABS.map((g) => <option key={g} value={g}>{g}%</option>)}
                  </select>
                  <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`purchase-item-remove-${i}`}
                    className="col-span-1 text-red-600 hover:text-red-700 flex justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setItems([...items, { product_id: "", qty: 1, rate: 0, gst_rate: null }])} data-testid="purchase-add-item-btn"
                className="text-xs text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add item
              </button>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 space-y-1.5 font-mono2 text-sm" data-testid="purchase-totals-card">
              <div className="flex justify-between text-slate-600"><span className="font-sans">Taxable value</span><span data-testid="purchase-taxable">{inr(taxable)}</span></div>
              <div className="flex justify-between text-slate-600"><span className="font-sans">GST (ITC claimable)</span><span className="text-blue-600" data-testid="purchase-gst">{inr(gstTotal)}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-1.5">
                <span className="font-sans text-slate-700 font-medium">Purchase total</span>
                <span className="font-bold text-lg text-emerald-600" data-testid="purchase-total">{inr(total)}</span>
              </div>
            </div>

            <Button type="submit" disabled={busy} data-testid="purchase-save-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
              {busy ? "Saving…" : editId ? "Save Changes" : "Post Purchase"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
